import type { InvitationTokenData } from "../invitation/InvitationTokenData.js";

export type SignupReferral = {
  token: string;
  data: Extract<InvitationTokenData, { __typename: "ReferralInfo" }>;
};

export interface SignupTokenProps {
  /** Referral presentation data (`r_` token) — shown above the form and forwarded as refToken. */
  referral: SignupReferral | null;
  /** A live CRM linking invitation (`i_` token) — forwarded as refToken, never presented. */
  refToken: string | null;
  /** The invited CRM company's name, seeding the organisation name field. */
  initialOrganisationName: string | null;
}

/**
 * What `AuthenticatedRoutes` hands to `CreateNewOrganisationPage` for a user who has no
 * organisation yet: a referral stays presentation data, a live CRM linking invitation becomes
 * the refToken the signup mutation accepts and seeds the organisation name with the invited
 * company, and every other invitation kind (membership, project contractee, spent/unknown)
 * contributes nothing — those are routed elsewhere.
 */
export function getCreateNewOrganisationTokenProps(
  token: string | null,
  tokenData: InvitationTokenData | null
): SignupTokenProps {
  if (!token || !tokenData) {
    return { referral: null, refToken: null, initialOrganisationName: null };
  }

  if (tokenData.__typename === "ReferralInfo") {
    return {
      referral: { token, data: tokenData },
      refToken: null,
      initialOrganisationName: null,
    };
  }

  if (tokenData.__typename === "CrmLinkingInvitation") {
    return {
      referral: null,
      refToken: token,
      initialOrganisationName: tokenData.contact.company,
    };
  }

  return { referral: null, refToken: null, initialOrganisationName: null };
}

export function getSignupRefToken(
  crmRefToken: string | null,
  referralToken: string | undefined
) {
  return crmRefToken ?? referralToken;
}

export function shouldRemoveSignupRefToken(
  crmRefToken: string | null,
  referralToken: string | undefined
) {
  return Boolean(crmRefToken || referralToken);
}
